import React from 'react';
import Navigation from 'components/Navigation';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import photoAccessibiliteSrc from 'images/accessibilite.jpg';
import { ReactComponent as CheckCircleFilled } from 'assets/icons/checkCircleFilled.svg';
import { ReactComponent as BulbFilled } from 'assets/icons/bulbFilled.svg';

export default function QuestionAccessibilite({ step, nextStep, lastStep }) {
  return (
    <div className="bg-white rounded-2xl md:p-8 p-4 mx-auto !max-w-3xl !min-h-none fadeIn align-center !mb-24 md:!mb-32 -mt-2">
      <h2 className="text-main !mb-4 !leading-none">
        Un logement plus accessible, une vie plus facile&nbsp;!
      </h2>
      <p className="italic text-gris-hellio !mb-5 !text-sm !leading-none">
        Vous êtes propriétaire ou locataire d’un logement que vous occupez au
        quotidien.
      </p>

      <div className="flex flex-col md:flex-row items-stretch w-full border gap-4 md:gap-8 !mb-5">
        <div className="md:w-1/2 w-full flex flex-col justify-center">
          <p className="align-left !text-sm text-gris-hellio">
            Votre habitation n’est plus adaptée à votre âge ou à votre situation
            de handicap, ce qui impacte votre confort et votre sécurité. Des
            travaux peuvent être réalisés pour améliorer votre cadre de vie et
            vous permettre de continuer à vivre sereinement chez vous.
          </p>
        </div>

        <div className="md:w-1/2 w-full flex overflow-hidden rounded-lg md:relative">
          <img
            src={photoAccessibiliteSrc}
            alt="Travaux d'accessibilité"
            className="md:absolute object-cover w-full md:-top-[25%]"
          />
        </div>
      </div>

      <div className="w-full rounded-2xl bg-vert-agriculture-1 !p-5 !mb-5">
        <h3 className="text-gris-hellio !mb-4 !text-base">
          Les conditions d’éligibilité
        </h3>

        <ul
          className={clsx(
            'text-gris-hellio list-inside text-left !m-auto md:w-[413px] !text-sm space-y-3',
            '[&>li]:flex [&>li]:items-center',
            '[&>li>svg]:mr-4 [&>li>svg]:min-w-6 [&>li>svg]:min-h-6',
            '[&>li>span>sup]:!leading-none',
          )}
        >
          <li>
            <CheckCircleFilled aria-hidden="true" />
            <span>
              Personne âgée à partir de 70 ans sans condition de GIR
              <sup>*</sup>
            </span>
          </li>
          <li>
            <CheckCircleFilled aria-hidden="true" />
            <span>
              Personne handicapée sans condition de GIR<sup>*</sup>
            </span>
          </li>
          <li>
            <CheckCircleFilled aria-hidden="true" />
            <span>
              Personne âgée de 60 à 69 ans (évaluation GIR
              <sup>*</sup>)
            </span>
          </li>
        </ul>
      </div>

      <div className="w-full rounded-2xl bg-bleu-hellio-1 !p-5 !mb-5">
        <h3 className="!text-gris-hellio !mb-4 !text-base text-left flex">
          <BulbFilled className="!mr-2" />
          Le rôle d’assistance à maitrise d’ouvrage
        </h3>
        <p className="!text-sm text-gris-hellio text-left">
          Il a pour mission de simplifier toutes vos démarches. Il réalise un
          diagnostic complet de votre logement afin de définir les travaux
          nécessaires pour favoriser votre autonomie.
        </p>
      </div>

      <div className="w-full rounded-lg bg-white !p-5 !mb-4 !border-1 !border-solid !border-bleu-hellio-1">
        <h3 className="text-gris-hellio !mb-4 !text-base">Les étapes</h3>

        <ol
          className={clsx(
            'list-inside md:flex md:gap-5 w-full flex-row md:text-center text-left !text-sm md:space-y-0 space-y-3',
            '[&>li]:flex [&>li]:text-bleu-hellio [&>li]:md:flex-col [&>li]:gap-3 [&>li]:basis-1/4 [&>li]:items-center',
            '[&>li>svg]:min-w-6 [&>li>svg]:min-h-6',
            '[&>li>span]:text-center [&>li>span]:bg-bleu-hellio [&>li>span]:text-white [&>li>span]:min-w-8 [&>li>span]:min-h-8 [&>li>span]:rounded-full [&>li>span]:!leading-8 [&>li>span]:!text-sm',
          )}
        >
          <li>
            <span aria-hidden="true">1</span>
            Inscription
          </li>
          <li>
            <span aria-hidden="true">2</span>
            Analyse des besoins en travaux par un AMO
          </li>
          <li>
            <span aria-hidden="true">3</span>
            Réalisation d’un devis avec subventions
          </li>
          <li>
            <span aria-hidden="true">4</span>
            Réalisation et suivi des travaux d’adaptation
          </li>
        </ol>
      </div>

      <p className="text-gris-hellio text-xs text-left m-0 [&>sup]:leading-none">
        <sup>*</sup>GIR&nbsp;: Autonomie gérontologique groupes iso-ressources
      </p>

      <Navigation
        data={true}
        step={step}
        nextStep={nextStep}
        lastStep={lastStep}
      />
    </div>
  );
}

QuestionAccessibilite.propTypes = {
  step: PropTypes.number,
  nextStep: PropTypes.func,
  lastStep: PropTypes.func,
};
